/**
 * LP-5358 / LP-5360: keeps an expanded transaction detail's `max-height` honest while its width
 * changes.
 *
 * <p><b>Why this is needed.</b> `showDetails` opens a row by writing
 * {@code max-height = scrollHeight + 'px'} once, and `.transaction-details` is `overflow: hidden`.
 * That was harmless while the content's height barely moved. It is not harmless now: the
 * container queries in `transaction-detail-layout.css` deliberately make the panel TALLER as it
 * narrows -- Sender/Receiver stop sharing a line, fields go 3-up to 2-up to 1-up, the cashback
 * table becomes stacked cards. So an already-open row that narrows (the window is dragged, or the
 * sidebar is toggled) keeps the height it had when it was wider and clips the rest.
 *
 * <p>Caught by Codex on PR #114 (P2). It is a regression introduced by the responsive work, not a
 * pre-existing bug. The visual harness forced `max-height: none` in order to render the expanded
 * state at all, which is exactly why measuring could not reveal it.
 *
 * <p><b>Width only.</b> The observer reacts to width changes and ignores height, because writing
 * `max-height` itself changes the height and reacting to that would be a feedback loop.
 *
 * <p><b>Lifetime.</b> A ResizeObserver stays alive while it has observation targets, so an
 * observer left pointing at a removed row would retain that detached element and its Angular view
 * -- a leak that accumulates every time pagination or a search replaces the list (Codex PR #114,
 * second round). Two mechanisms close it, deliberately overlapping:
 *
 * <ul>
 *   <li>{@link disconnectExpandedDetails} is called from each host component's {@code ngOnDestroy}
 *       and whenever it replaces its list. This is the guaranteed path.
 *   <li>The callback also self-disconnects when it sees a detached element. Removing a node
 *       usually fires one final 0x0 notification, so in practice most rows clean themselves up
 *       immediately -- but that is a convenience, not something the spec promises, which is why
 *       the explicit path above exists.
 * </ul>
 */
const observers = new Map<HTMLElement, ResizeObserver>();

/**
 * Attaches a {@link ResizeObserver} to a `.transaction-details` element, once.
 *
 * <p>Safe to call on every expand. No-ops where `ResizeObserver` is unavailable, leaving the
 * previous behaviour rather than throwing.
 */
export function observeExpandedDetail(element: HTMLElement | null): void {
    if (!element || typeof ResizeObserver === 'undefined' || observers.has(element)) {
        return;
    }

    let lastWidth = element.clientWidth;

    const observer = new ResizeObserver(() => {
        // The row was removed from the document -- stop holding it.
        if (!element.isConnected) {
            disconnectExpandedDetail(element);
            return;
        }

        const width = element.clientWidth;
        if (width === lastWidth) {
            return;
        }
        lastWidth = width;

        // An empty max-height means the row is collapsed; leave it collapsed.
        if (!element.style.maxHeight) {
            return;
        }

        // scrollHeight reports the FULL content height even while max-height is clipping it, so
        // this re-measures the reflowed content rather than the clipped box.
        element.style.maxHeight = element.scrollHeight + 'px';
    });

    observers.set(element, observer);
    observer.observe(element);
}

/** Stops observing one element. Safe to call for an element that was never observed. */
export function disconnectExpandedDetail(element: HTMLElement | null): void {
    if (!element) {
        return;
    }
    const observer = observers.get(element);
    if (observer) {
        observer.disconnect();
        observers.delete(element);
    }
}

/**
 * Stops observing rows belonging to {@code root}, plus any element already detached.
 *
 * <p>Call from a host component's {@code ngOnDestroy} and before it tears down or replaces its
 * list, passing the component's own host element.
 *
 * <p><b>Scoped deliberately.</b> An earlier version cleared the registry wholesale. That is wrong
 * in two ways Codex found (PR #114): a delayed callback firing after {@code ngOnDestroy} would
 * clear observers belonging to a different, newly active list, and it made each host responsible
 * for everyone else's state. Containment is checked instead, so a host can only ever release its
 * own rows. Detached elements are always released, whoever owned them -- they are the leak.
 *
 * <p>Omitting {@code root} releases everything, which is for teardown in tests.
 */
export function disconnectExpandedDetails(root?: HTMLElement | null): void {
    observers.forEach((observer, element) => {
        if (!root || !element.isConnected || root.contains(element)) {
            observer.disconnect();
            observers.delete(element);
        }
    });
}

/** Test seam: how many elements are currently observed. */
export function observedExpandedDetailCount(): number {
    return observers.size;
}
